/**
 * Shadow of types and enums from `roboto.query.api`
 */

import { TargetEntityType } from "@/shared/domain/customFields";
import { ISO_8601_DATETIME } from "@/types";
import { SearchQueryBody } from "@/types/search";

export enum QueryTarget {
  Collections = "collections",
  Datasets = "datasets",
  Devices = "devices",
  Events = "events",
  Files = "files",
  Sessions = "sessions",
  Topics = "topics",
  TopicMessagePaths = "topic_message_paths",
}

export function queryTargetToDisplayName(target: QueryTarget) {
  if (target === QueryTarget.TopicMessagePaths) {
    return "Message Paths";
  }

  return target;
}

/**
 * Maps a query target to the custom-field `TargetEntityType` whose Ready
 * fields the RoboQL editor should offer, or `undefined` for targets without
 * custom-field search+sort.
 */
export function queryTargetToEntityType(
  target: QueryTarget,
): TargetEntityType | undefined {
  switch (target) {
    case QueryTarget.Datasets:
      return TargetEntityType.Dataset;
    case QueryTarget.Events:
      return TargetEntityType.Event;
    default:
      return undefined;
  }
}

export enum QueryStatus {
  Failed = "failed",
  ResultsAvailable = "results_available",
  Scheduled = "scheduled",
}

export enum QueryScheme {
  QuerySpecV1 = "query_spec_v1",
}

export enum QueryContentMode {
  RecordWithMeta = "record_with_meta",
  RecordOnly = "record_only",
}

export interface QueryRecord {
  modified: ISO_8601_DATETIME;
  org_id: string;
  query_id: string;
  query_ctx: {
    query_scheme: QueryScheme;
    query: Record<string, unknown>;
  };
  status: QueryStatus;
  submitted: ISO_8601_DATETIME;
  target: QueryTarget;
}

export function getQueryContentMode(record: QueryRecord): QueryContentMode {
  const contentMode = record.query_ctx.query?.["content_mode"];

  if (
    contentMode === undefined ||
    contentMode === null ||
    !(typeof contentMode === "string")
  ) {
    return QueryContentMode.RecordWithMeta;
  }

  return contentMode as QueryContentMode;
}

export interface QualifiedRoboqlQuery {
  query: string;
  target: QueryTarget;
}

export interface SubmitStructuredQueryRequest {
  content_mode: QueryContentMode;
  query: SearchQueryBody;
  target: QueryTarget;
}

export interface SubmitRoboqlQueryRequest {
  content_mode: QueryContentMode;
  query: string;
  target: QueryTarget;
}

export interface SubmitTermQueryRequest {
  content_mode: QueryContentMode;
  term: string;
  target: QueryTarget;
}

export interface MetadataValuesRequest {
  target: QueryTarget;
  metadata_key: string;
  entity_ids: string[];
}

export interface MetadataValuesResponse {
  metadata_key: string;
  metadata_values: Record<string, unknown>;
}
